// @ts-nocheck
// Self Ticket Request Types - matching backend DTOs

export enum SelfTicketItemType {
  COURSE = 'COURSE',
  CERTIFICATION = 'CERTIFICATION',
  LEARNING_PATH = 'LEARNING_PATH',
  GENERAL = 'GENERAL',
}

export enum SelfTicketRequestStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  DENIED = 'DENIED',
  CLOSED = 'CLOSED',
}

// Item in a self-ticket request
export interface SelfTicketItemDto {
  item_id: string;
  item_name: string;
  item_type?: SelfTicketItemType; // Optional type identifier (COURSE, LEARNING_PATH, CERTIFICATION)
  status?: SelfTicketRequestStatus; // Item-level status (PENDING, APPROVED, DENIED)
}

// Course generation config for GENERAL tickets with COURSE suggestion type
export interface CourseGenerationConfig {
  content_delivery_type: 'html' | 'html_with_audio' | 'video_presentation';
  design_theme: 'educational' | 'modern' | 'corporate' | 'dark' | 'minimal';
  module_generation_config: {
    min_modules: number;
    max_modules: number;
  };
  content_generation_config: {
    min_content_items_per_module: number;
    max_content_items_per_module: number;
  };
}

// Request body for creating a self-ticket request
export interface CreateSelfTicketRequestDto {
  item_type: SelfTicketItemType;
  items: SelfTicketItemDto[];
  request_message?: string;
  general_description?: string;
  requested_suggestion_type?: SelfTicketItemType;
  certification_courses?: SelfTicketItemDto[];
  certification_learning_paths?: SelfTicketItemDto[];
  language_code?: string;
  course_generation_config?: CourseGenerationConfig;
}

// Query parameters for listing self-ticket requests
export interface ListSelfTicketRequestsQueryDto {
  status?: SelfTicketRequestStatus;
  item_type?: SelfTicketItemType;
  my_requests_only?: boolean;
  approver_user_id?: string;
  search?: string;
  page?: number;
  limit?: number;
}

// Approver information in a request
export interface SelfTicketApprover {
  user_id: string;
  user_name: string;
  email: string;
  role: 'BU_HEAD' | 'REPORTING_MANAGER' | 'HRBP' | 'ADMIN';
  status: SelfTicketRequestStatus;
  action_at?: string;
  comments?: string;
  is_read?: boolean;
  read_at?: string;
  profile_image?: string;
}

// Read by tracking (who viewed the ticket and when)
export interface SelfTicketReadBy {
  user_id: string;
  user_name: string;
  read_at: string;
}

// Actioned by information (who approved/denied/suggested the request)
export interface ActionedByDto {
  role: string;
  email: string;
  user_id: string;
  user_name: string;
  action: 'APPROVED' | 'DENIED' | 'SUGGESTED';
  actioned_at: string;
  remarks?: string;
}

// Message reaction in a ticket chat
export interface MessageReactionDto {
  emoji: string;
  user_ids: string[];
  count: number;
}

// Message in a ticket chat
export interface TicketMessageDto {
  _id: string;
  sender_id: string;
  sender_name: string;
  sender_role: 'REQUESTER' | 'BU_HEAD' | 'REPORTING_MANAGER' | 'HRBP' | 'ADMIN' | 'SYSTEM';
  message: string;
  sent_at: string;
  is_read_by: string[];
  reactions?: MessageReactionDto[];
  is_deleted?: boolean;
}

// Single self-ticket request response
export interface SelfTicketRequestResponseDto {
  _id: string;
  ticket_number: string;
  requester_id: string;
  requester_email: string;
  requester_name: string;
  requester_profile_image?: string;
  item_type: SelfTicketItemType;
  items: SelfTicketItemDto[];
  items_count: number;
  request_message?: string;
  general_description?: string;
  requested_suggestion_type?: SelfTicketItemType;
  certification_courses?: SelfTicketItemDto[];
  certification_learning_paths?: SelfTicketItemDto[];
  language_code?: string;
  course_generation_config?: CourseGenerationConfig;
  status: SelfTicketRequestStatus;
  is_read: boolean;
  unread_message_count: number;
  general_description?: string;
  approvers?: SelfTicketApprover[];
  messages?: TicketMessageDto[];
  read_by?: SelfTicketReadBy[];
  actioned_by?: ActionedByDto;
  approved_by?: {
    user_id: string;
    user_name: string;
    role: string;
  };
  denied_by?: {
    user_id: string;
    user_name: string;
    role: string;
    reason?: string;
  };
  createdAt: string;
  updatedAt: string;
  resolved_at?: string;
  closed_at?: string;
  closed_by?: {
    user_id: string;
    user_name: string;
  };
}

// Paginated list response
export interface PaginatedSelfTicketRequestListDto {
  data: SelfTicketRequestResponseDto[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
}

// Available course for request (from assignment-analytics/courses)
export interface AvailableCourse {
  course_id: string;
  course_title: string;
  description?: string;
  thumbnail_image_url?: string;
  banner_image_url?: string;
  difficulty?: string;
  estimated_duration?: {
    hours: number;
    minutes: number;
    total_minutes: number;
  };
}

// Available certification for request
export interface AvailableCertification {
  certification_id: string;
  title: string;
  description?: string;
  thumbnail_image_url?: string;
  time_limit_minutes?: number;
  max_attempts?: number;
}

// Available learning path for request
export interface AvailableLearningPath {
  learning_path_id: string;
  title: string;
  description?: string;
  thumbnail_image_url?: string;
  total_courses: number;
  estimated_duration?: {
    hours: number;
    minutes: number;
  };
}

// Query parameters for listing available items
export interface ListAvailableItemsQueryDto {
  search?: string;
  page?: number;
  limit?: number;
}

// Available item from API response
export interface AvailableItemDto {
  _id: string;
  title: string;
  description?: string;
  banner_image_url?: string;
  thumbnail_image_url?: string;
}

// Enhanced course item with modules, content, and assessment details
export interface CourseContentItemSummary {
  title: string;
}

export interface CourseModuleSummary {
  title: string;
  description: string;
  content_item_count: number;
  content_items: CourseContentItemSummary[];
}

export interface CourseAssessmentSummary {
  course_pre: boolean;
  course_post: boolean;
  module_pre_count: number;
  module_post_count: number;
  content_pre_count: number;
  content_post_count: number;
}

export interface CourseEstimatedDuration {
  hours: number;
  minutes: number;
}

export interface AvailableCourseItemDto extends AvailableItemDto {
  difficulty?: string;
  module_count: number;
  estimated_duration?: CourseEstimatedDuration;
  modules: CourseModuleSummary[];
  assessment_summary: CourseAssessmentSummary;
}

// Paginated response for available items
export interface PaginatedAvailableItemsDto {
  data: AvailableItemDto[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
}

// Paginated response specifically for available courses
export interface PaginatedAvailableCoursesDto {
  data: AvailableCourseItemDto[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
}

// Enhanced certification item with exam details, required courses, and learning paths
export interface CertificationLinkedItem {
  _id: string;
  title: string;
}

export interface AvailableCertificationItemDto extends AvailableItemDto {
  total_questions: number;
  time_limit_minutes?: number;
  passing_score: number;
  max_attempts: number;
  required_courses: CertificationLinkedItem[];
  learning_paths: CertificationLinkedItem[];
}

// Paginated response specifically for available certifications
export interface PaginatedAvailableCertificationsDto {
  data: AvailableCertificationItemDto[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
}

// Enhanced learning path item with embedded course details
export interface LearningPathCourseSummary {
  course_id: string;
  order: number;
  course_title: string;
  course_description: string;
  difficulty_level: string;
}

export interface AvailableLearningPathItemDto extends AvailableItemDto {
  total_courses: number;
  difficulty_progression?: string;
  courses: LearningPathCourseSummary[];
}

// Paginated response specifically for available learning paths
export interface PaginatedAvailableLearningPathsDto {
  data: AvailableLearningPathItemDto[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
}

// Stats response for self-ticket requests (approver view)
export interface SelfTicketRequestStatsDto {
  total_count: number;
  pending_count: number;
  approved_count: number;
  denied_count: number;
}
